import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Button, NotFound, useToast } from '@app/core';
import { Input } from '@app/form';
import { QuoteFoldableBlock } from './components/QuoteFoldableBlock';
import { QuoteRichTextEditor } from './components/QuoteRichTextEditor';
import { QuoteSidebar } from './components/QuoteSidebar';
import {
  canLaunchStudyFromQuote,
  missingQuoteBlocks,
  openQuoteDocument,
  QuoteStatus,
  RenderedQuoteTexts,
  useIsAdmin,
  useQuoteTexts,
  useRenderQuoteTexts,
  useQuote,
  useQuoteProspect,
  useCreateStudyFromQuote,
  useSaveQuoteProducts,
  useUpdateQuoteStatus,
} from 'queries';
import { formatLocale, getLocale, supportedLocales } from 'i18n';
import { shouldApplyVat } from 'lib/billing';
import { QuoteLayout } from './QuoteLayout';
import { ChangeEvent } from 'preact/compat';
import { ToggleGroup } from 'components/ToggleGroup';
import { QuoteLinesTable } from './QuoteLinesTable';
import { QuotePreview } from './QuotePreview';
import {
  applyParticipantCount,
  linesToDto,
  QuoteLine,
  quoteToLines,
  relocalizeLines,
} from './types';

const useStyles = createUseStyles((theme: Theme) => ({
  block: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
    padding: theme.marginBase * 3,
    marginBottom: theme.marginBase * 3,
  },
  blockTitle: {
    ...theme.fonts.h4,
    display: 'flex',
    gap: theme.marginBase,
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 2,
  },
  searchLine: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
  },
  searchInput: {
    width: 320,
  },
  participants: {
    width: 140,
  },
  hint: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
  },
  fullpage: {
    height: '100vh',
  },
  content: {
    width: '100%',
    margin: 'auto',
    padding: 0,
  },
  informationBox: {
    padding: theme.marginBase,
    marginTop: theme.marginBase * 3,
  },
  contentInfo: {
    whiteSpace: 'pre-line',
  },
  adminLink: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 4,
    color: theme.colors.purpleAccessible,
  },
  button: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
    padding: 0,
    paddingLeft: theme.marginBase / 2,
    margin: 0,
    width: 'fit-content',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  navTitle: {
    marginTop: theme.marginBase * 2,
  },
  participantsInput: {
    width: theme.marginBase * 20,
  },
}));

const toQuoteLocale = (locale?: string) => {
  const formatted = formatLocale(locale || getLocale() || 'en');
  return supportedLocales.includes(formatted) ? formatted : 'en';
};

export const QuotePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();

  const { idOrRef } = useParams<{ idOrRef?: string }>();
  const { data: prospect } = useQuoteProspect(idOrRef);

  const [locale, setLocale] = useState(toQuoteLocale());

  const { data: quote, isFetching } = useQuote(idOrRef, locale);
  const [loadedQuoteId, setLoadedQuoteId] = useState<string | undefined>();
  const [numberOfParticipants, setNumberOfParticipants] = useState(0);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [replacedIds, setReplacedIds] = useState<string[]>([]);
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [vat, setVat] = useState(true);
  const vatChosen = useRef(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingStudy, setCreatingStudy] = useState(false);

  const launchable = canLaunchStudyFromQuote(quote);
  const readOnly = launchable || !!quote?.studyId;
  const showPreview = previewing || readOnly;

  const { data: texts } = useQuoteTexts(idOrRef, locale);
  const [rendered, setRendered] = useState<RenderedQuoteTexts>();
  const [rendering, setRendering] = useState(false);
  const { mutateAsync: renderTexts } = useRenderQuoteTexts(idOrRef);
  const missingInHeader = missingQuoteBlocks(header, texts?.header);
  const missingInFooter = missingQuoteBlocks(footer, texts?.footer);
  const incomplete = !!missingInHeader.length || !!missingInFooter.length;

  const toast = useToast();
  const { mutateAsync: saveProducts } = useSaveQuoteProducts(idOrRef);
  const { mutateAsync: updateStatus } = useUpdateQuoteStatus(idOrRef);
  const { mutateAsync: createStudy } = useCreateStudyFromQuote(idOrRef);

  const viewOptions = useMemo(
    () => [
      { value: 'edition' as const, label: t('quote.edition') },
      { value: 'preview' as const, label: t('quote.preview') },
    ],
    [t],
  );

  useEffect(() => {
    if (prospect?.locale) {
      setLocale(toQuoteLocale(prospect.locale));
    }
  }, [prospect]);

  useEffect(() => {
    if (!prospect || vatChosen.current) {
      return;
    }
    setVat(shouldApplyVat(prospect.billingCountryCode ?? ''));
  }, [prospect]);

  const onVatToggle = useCallback((checked: boolean) => {
    vatChosen.current = true;
    setVat(checked);
  }, []);

  useEffect(() => {
    if (!quote || quote.id === loadedQuoteId) {
      return;
    }
    setLoadedQuoteId(quote.id);
    setNumberOfParticipants(quote.numberOfParticipantRequired);
    setHeader(quote.headerText || '');
    setFooter(quote.footerText || '');
    const loaded = quoteToLines(
      quote.products,
      quote.duration,
      quote.numberOfParticipantRequired,
    );
    setLines(loaded);
    setReplacedIds(loaded.flatMap((line) => line.sourceIds || []));
  }, [quote, loadedQuoteId]);

  useEffect(() => {
    setLines((curr) => relocalizeLines(curr, quote?.products));
  }, [quote?.products]);

  useEffect(() => {
    if (!showPreview || !loadedQuoteId) {
      return;
    }

    let obsolete = false;
    setRendering(true);
    renderTexts({
      header: header || undefined,
      footer: footer || undefined,
      locale,
    })
      .then((printed) => !obsolete && setRendered(printed))
      .catch((e) => !obsolete && toast.exception(e))
      .finally(() => !obsolete && setRendering(false));

    return () => {
      obsolete = true;
    };
  }, [showPreview, loadedQuoteId, header, footer, locale, renderTexts, toast]);

  const onParticipantsChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number((event.currentTarget || event.target).value) || 0;
      setNumberOfParticipants(value);
      setLines((curr) => applyParticipantCount(curr, value));
    },
    [],
  );

  const onStatusChange = useCallback(
    async (status: string) => {
      try {
        await updateStatus({ status: status as QuoteStatus });
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [updateStatus, toast],
  );

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const savedQuote = await saveProducts({
        lines: linesToDto(lines),
        replacedIds,
        numberOfParticipantRequired: numberOfParticipants,
        headerText: header,
        footerText: footer,
      });

      const saved = quoteToLines(
        savedQuote.products,
        savedQuote.duration,
        savedQuote.numberOfParticipantRequired,
      );
      setLines(saved);
      setReplacedIds(saved.flatMap((line) => line.sourceIds || []));
      toast.saved();
    } catch (e) {
      toast.exception(e);
    } finally {
      setSaving(false);
    }
  }, [
    footer,
    header,
    lines,
    numberOfParticipants,
    replacedIds,
    saveProducts,
    toast,
  ]);

  const onCreateStudy = useCallback(async () => {
    setCreatingStudy(true);
    try {
      const { id } = await createStudy({});
      toast.saved();
      navigate(`/sessions/edit/${id}/settings`);
    } catch (e) {
      toast.exception(e);
    } finally {
      setCreatingStudy(false);
    }
  }, [createStudy, navigate, toast]);

  if (!isAdmin) {
    return <NotFound />;
  }

  return (
    <QuoteLayout
      navTitle={t('quote.quote.title', {
        ref: quote?.ref ? `${quote?.ref}` : '',
      })}
      fetching={isFetching || rendering}
      sidebar={
        !!quote && (
          <QuoteSidebar
            quote={quote}
            prospect={prospect}
            locale={locale}
            onLocaleChange={setLocale}
            vat={vat}
            onVatToggle={onVatToggle}
            onStatusChange={onStatusChange}
          />
        )
      }
      titleSideElement={
        !!quote &&
        !readOnly && (
          <ToggleGroup
            options={viewOptions}
            selectedValue={previewing ? 'preview' : 'edition'}
            onChange={(value) => {
              setRendered(undefined);
              setPreviewing(value === 'preview');
            }}
            color="blue"
          />
        )
      }
      actions={
        <>
          <Button
            color="accent"
            variant="muted"
            onClick={() => navigate('/quote/new')}
          >
            {t('quote.create.button')}
          </Button>
          {!!quote && (
            <>
              <Button
                color="blue"
                variant="muted"
                onClick={() =>
                  openQuoteDocument(quote.ref || quote.id, locale, vat)
                }
              >
                {t('quote.downloadPdf')}
              </Button>
              {quote.studyId ? (
                <Button
                  color="green"
                  onClick={() =>
                    navigate(`/sessions/details/${quote.studyId}`)
                  }
                >
                  {t('quote.openStudy')}
                </Button>
              ) : launchable ? (
                <Button
                  color="green"
                  onClick={onCreateStudy}
                  isLoading={creatingStudy}
                >
                  {t('quote.createStudy')}
                </Button>
              ) : (
                <Button
                  color="green"
                  onClick={onSave}
                  isLoading={saving}
                  disabled={!lines.length || incomplete}
                >
                  {t('quote.save')}
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      <div>
        {!!idOrRef && !quote && !isFetching && (
          <div className={classes.hint}>
            {t('quote.notFound', { ref: idOrRef })}
          </div>
        )}
      </div>

      {!!quote &&
        quote.status === QuoteStatus.ACCEPTED &&
        !readOnly &&
        !!quote.missingStudyFields?.length && (
          <div className={classes.hint}>
            {t('quote.missingStudyFields', {
              fields: quote.missingStudyFields
                .map((field) => t(`quote.studyFields.${field}`))
                .join(', '),
            })}
          </div>
        )}

      {!!quote &&
        (showPreview ? (
          <QuotePreview
            locale={locale}
            quote={quote}
            prospect={prospect}
            vat={vat}
            headerHtml={rendered?.header}
            footerHtml={rendered?.footer}
            lines={lines}
          />
        ) : (
          <>
            <QuoteFoldableBlock title={t('quote.header.title')}>
              <QuoteRichTextEditor
                value={header}
                onChange={setHeader}
                info={texts?.header}
                missingBlocks={missingInHeader}
                hint={t('quote.header.hint')}
              />
            </QuoteFoldableBlock>

            <div className={classes.block}>
              <div className={classes.blockTitle}>
                {t('quote.products')}
                <div className={classes.participantsInput}>
                  <Input
                    noMargin
                    type="number"
                    label={t('quote.participants')}
                    value={numberOfParticipants}
                    onChange={onParticipantsChange}
                  />
                </div>
              </div>
              <QuoteLinesTable
                lines={lines}
                duration={quote.duration}
                numberOfParticipants={numberOfParticipants}
                locale={locale}
                vat={vat}
                onChange={setLines}
              />
            </div>

            <QuoteFoldableBlock title={t('quote.footer.title')}>
              <QuoteRichTextEditor
                value={footer}
                onChange={setFooter}
                info={texts?.footer}
                missingBlocks={missingInFooter}
                hint={t('quote.footer.hint')}
              />
            </QuoteFoldableBlock>
          </>
        ))}
    </QuoteLayout>
  );
};
